/**
 * Generated by orval v8.17.0 🍺
 * Do not edit manually.
 * AOS Pathways API
 * OpenAPI spec version: 26.6.3
 */
/**
 * Direction of metric trend compared to previous period.
 */
export type TrendDirection = (typeof TrendDirection)[keyof typeof TrendDirection];

export const TrendDirection = {
  up: "up",
  down: "down",
  flat: "flat",
} as const;

/**
 * A metric value with trend information.
 */
export interface MetricTrend {
  value: number;
  direction: TrendDirection;
  label?: string | null;
}

/**
 * A single point in a time series chart.
 */
export interface TimeSeriesPoint {
  date: string;
  value: number;
}

/**
 * Response for the AI Usage tab.
 */
export interface AIUsageResponse {
  queries_per_hour: number;
  queries_trend: MetricTrend;
  total_conversations: number;
  usage_trend: TimeSeriesPoint[];
}

/**
 * Spec 078: persist an interrupted assistant message.
 */
export interface AddInterruptedMessageRequest {
  content?: string;
  message_id?: string | null;
}

/**
 * One row in the top-errors panel.
 */
export interface ErrorCount {
  code: string;
  count: number;
}

/**
 * Per-tool aggregated usage metrics for the admin dashboard.
 */
export interface ToolUsageMetricsResponse {
  tool_name: string;
  invocations: number;
  unique_users: number;
  p50_latency_ms: number;
  p95_latency_ms: number;
  cache_hit_rate: number;
  success_count: number;
  empty_count: number;
  error_count: number;
  top_errors?: ErrorCount[];
}

/**
 * One daily bucket in the invocation time-series chart.
 */
export interface TimeSeriesPointResponse {
  date: string;
  tool_name: string;
  invocations: number;
}

/**
 * Full analytics payload consumed by the admin dashboard page.
 */
export interface AgentToolAnalyticsResponse {
  tools: ToolUsageMetricsResponse[];
  time_series: TimeSeriesPointResponse[];
  window_from: string;
  window_to: string;
}

/**
 * One tool row on the admin management page.
 */
export interface AgentToolDescriptorResponse {
  /** Unique tool name from REGISTRY */
  name: string;
  /** Human-readable tool description */
  description: string;
  /** Whether the tool is enabled right now */
  enabled: boolean;
  /** When the enablement was last changed */
  updated_at?: string | null;
  /** Admin who last toggled the tool */
  updated_by_email?: string | null;
}

/**
 * PATCH body for enabling/disabling a tool.
 */
export interface AgentToolToggleRequest {
  enabled: boolean;
}

export type ApprovalWorkflowResponseByType = { [key: string]: number };

/**
 * Response for approval workflow metrics endpoint.
 */
export interface ApprovalWorkflowResponse {
  pending_creation_count: number;
  pending_completion_count: number;
  avg_time_to_approval_hours?: number | null;
  approval_rate?: number | null;
  by_type?: ApprovalWorkflowResponseByType;
  trend?: TimeSeriesPoint[];
}

/**
 * Request to approve a task completion.
 */
export interface ApproveCompletionRequest {
  feedback?: string | null;
}

/**
 * Request to assign or remove a manager for a user.
 */
export interface AssignManagerRequest {
  /** UID of the manager to assign. Set to null to remove manager. */
  manager_id?: string | null;
}

/**
 * User role levels in the application.
 */
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const UserRole = {
  user: "user",
  manager: "manager",
  director: "director",
  administrator: "administrator",
  super_admin: "super_admin",
} as const;

/**
 * Authentication provider types.
 */
export type AuthProvider = (typeof AuthProvider)[keyof typeof AuthProvider];

export const AuthProvider = {
  microsoft: "microsoft",
  email_password: "email_password",
} as const;

/**
 * User department for functional segmentation (Sales, Service, Operations).
 */
export type Department = (typeof Department)[keyof typeof Department];

export const Department = {
  sales: "sales",
  service: "service",
  operations: "operations",
} as const;

export interface UserProfileResponse {
  uid: string;
  email: string;
  display_name?: string | null;
  job_title?: string | null;
  photo_url?: string | null;
  role: UserRole;
  provider: AuthProvider;
  created_at: string;
  last_login_at: string;
  manager_id?: string | null;
  manager_name?: string | null;
  department?: Department | null;
}

/**
 * Response after assigning/removing a manager.
 */
export interface AssignManagerResponse {
  user: UserProfileResponse;
  message?: string;
}

export interface AuthCallbackRequest {
  /** @minLength 1 */
  firebase_token: string;
  /** Microsoft OAuth access token for Graph API calls */
  microsoft_access_token?: string | null;
}

export interface AuthCallbackResponse {
  user: UserProfileResponse;
  session_expires_at?: string | null;
  message?: string;
  /** If true, client should refresh Firebase token and retry auth callback */
  token_refresh_required?: boolean;
}

/**
 * Body for ``PATCH /admin/integrations/autotask``.
 */
export interface AutoTaskAdminConfigRequest {
  /**
   * @minLength 1
   * @maxLength 200
   */
  username: string;
  /**
   * @minLength 1
   * @maxLength 500
   */
  secret: string;
  /**
   * @minLength 1
   * @maxLength 200
   */
  integration_code: string;
}

/**
 * Response for ``GET /admin/integrations/autotask``.
 */
export interface AutoTaskAdminStatus {
  configured: boolean;
  healthy?: boolean;
  configured_at?: string | null;
  configured_by?: string | null;
}

export type AutoTaskPayloadProperties = { [key: string]: unknown };

export type AutoTaskPayloadEnhanced = { [key: string]: unknown };

/**
 * Kind-specific payload for an AutoTask PSA entity (spec 075).
 */
export interface AutoTaskPayload {
  kind?: "autotask";
  /** @maxLength 64 */
  entity_type: string;
  /** @maxLength 64 */
  entity_id: string;
  properties?: AutoTaskPayloadProperties;
  enhanced?: AutoTaskPayloadEnhanced;
  snapshot_taken_at?: string | null;
}

/**
 * Response containing signed download URL for avatar.
 */
export interface AvatarDownloadResponse {
  /** Signed URL for downloading the avatar */
  download_url: string;
  /** URL expiration in seconds */
  expires_in: number;
}

/**
 * Status of an avatar throughout its lifecycle.
 */
export type AvatarStatus = (typeof AvatarStatus)[keyof typeof AvatarStatus];

export const AvatarStatus = {
  uploading: "uploading",
  validating: "validating",
  validated: "validated",
  generating: "generating",
  ready: "ready",
  failed: "failed",
} as const;

/**
 * Available avatar styles.
 */
export type AvatarStyle = (typeof AvatarStyle)[keyof typeof AvatarStyle];

export const AvatarStyle = {
  photorealistic: "photorealistic",
  artistic: "artistic",
} as const;

/**
 * Target career level for avatar generation.
 */
export type CareerLevel = (typeof CareerLevel)[keyof typeof CareerLevel];

export const CareerLevel = {
  senior: "senior",
  manager: "manager",
  director: "director",
  executive: "executive",
} as const;

/**
 * Full avatar response with all details.
 */
export interface AvatarResponse {
  id: string;
  status: AvatarStatus;
  selected_style?: AvatarStyle | null;
  photorealistic_url?: string | null;
  artistic_url?: string | null;
  target_career_level: CareerLevel;
  years_into_future: number;
  custom_context?: string | null;
  regeneration_count?: number;
  created_at: string;
  updated_at: string;
}

/**
 * Response with current avatar status and URLs.
 */
export interface AvatarStatusResponse {
  avatar_id: string;
  status: AvatarStatus;
  /** Generation progress (0-100) */
  progress_percent?: number | null;
  /** Error message if failed */
  error_message?: string | null;
  /** Signed download URL for photorealistic version */
  photorealistic_url?: string | null;
  /** Signed download URL for artistic version */
  artistic_url?: string | null;
}

/**
 * Request for generating a signed upload URL for photo upload.
 */
export interface AvatarUploadUrlRequest {
  /**
   * Original filename
   * @maxLength 255
   */
  filename: string;
  /**
   * MIME type (image/jpeg, image/png, or image/webp)
   * @pattern ^image/(jpeg|png|webp)$
   */
  content_type: string;
}

/**
 * Response containing signed upload URL.
 */
export interface AvatarUploadUrlResponse {
  /** Signed URL for direct upload to GCS */
  upload_url: string;
  /** Created avatar record ID */
  avatar_id: string;
  /** URL expiration in seconds */
  expires_in: number;
}

export interface BodyUploadCustomIconAdminCoachingRolesRoleIdIconPost {
  file: Blob;
}

export interface BodyUploadFileFilesUploadPost {
  file: Blob;
}

export interface BodyUploadTemplateFileAdminDocumentGenerationTemplatesTemplateIdFilePost {
  file: Blob;
}

/**
 * Request to approve multiple items at once.
 */
export interface BulkApprovalRequest {
  goal_ids?: string[] | null;
  milestone_ids?: string[] | null;
  task_ids?: string[] | null;
}

/**
 * Response from bulk approval.
 */
export interface BulkApprovalResponse {
  approved_count: number;
  failed_ids: string[];
  errors: string[];
}

/**
 * Single department assignment for bulk update.
 */
export interface BulkDepartmentAssignment {
  /** @minLength 1 */
  uid: string;
  department?: Department | null;
}

/**
 * Error detail for failed bulk update.
 */
export interface BulkUpdateDepartmentError {
  uid: string;
  error: string;
}

/**
 * Request to bulk update user departments.
 */
export interface BulkUpdateDepartmentRequest {
  /**
   * @minItems 1
   * @maxItems 100
   */
  assignments: BulkDepartmentAssignment[];
}

/**
 * Response from bulk department update.
 */
export interface BulkUpdateDepartmentResponse {
  updated_count: number;
  errors?: BulkUpdateDepartmentError[];
}

/**
 * Type of source for a calendar event.
 */
export type CalendarEventSourceType =
  (typeof CalendarEventSourceType)[keyof typeof CalendarEventSourceType];

export const CalendarEventSourceType = {
  goal: "goal",
  task: "task",
  training: "training",
} as const;

/**
 * Sync status for individual calendar events.
 */
export type CalendarEventSyncStatus =
  (typeof CalendarEventSyncStatus)[keyof typeof CalendarEventSyncStatus];

export const CalendarEventSyncStatus = {
  synced: "synced",
  pending: "pending",
  error: "error",
} as const;

/**
 * Represents a synced calendar event mapping.
 */
export interface CalendarEvent {
  /** Document ID */
  id: string;
  /** User's Firebase UID */
  user_id: string;
  /** Type of source (goal, task, training) */
  source_type: CalendarEventSourceType;
  /** Goal/Task/Training ID */
  source_id: string;
  /** Microsoft Graph event ID */
  outlook_event_id: string;
  /** Calendar ID */
  outlook_calendar_id: string;
  /** Event title */
  event_subject: string;
  /** Event start time */
  event_start: string;
  /** Event end time */
  event_end: string;
  /** Last sync timestamp */
  synced_at: string;
  /** Sync status */
  sync_status: CalendarEventSyncStatus;
  /** Error message if failed */
  sync_error?: string | null;
}

/**
 * Response for listing synced calendar events.
 */
export interface CalendarEventListResponse {
  events: CalendarEvent[];
  total: number;
}

/**
 * Stored call analysis record with all scores and metadata.
 */
export interface CallAnalysis {
  id: string;
  user_id: string;
  conversation_id: string;
  transcript_id?: string | null;
  coaching_role_id: string;
  overall_score?: number | null;
  tier?: string | null;
  agenda_set_score?: number | null;
  clear_objective_score?: number | null;
  client_success_defined_score?: number | null;
  positive_tone_score?: number | null;
  relationship_level_score?: number | null;
  persona_wisdom_score?: number | null;
  question_variety_score?: number | null;
  storytelling_score?: number | null;
  third_party_examples_score?: number | null;
  bant_qualification_score?: number | null;
  objection_handling_score?: number | null;
  value_articulation_score?: number | null;
  sme_expertise_score?: number | null;
  professionalism_score?: number | null;
  listen_talk_ratio_score?: number | null;
  communication_clarity_score?: number | null;
  tied_to_outcomes_score?: number | null;
  gained_commitment_score?: number | null;
  next_steps_timeline_score?: number | null;
  rep_owns_ball_score?: number | null;
  talk_time_percentage?: number | null;
  call_duration_minutes?: number | null;
  relationship_status?: string | null;
  prospect_name?: string | null;
  prospect_company?: string | null;
  call_outcome?: string | null;
  areas_to_improve?: string[];
  key_insights?: string[];
  created_at: string;
}

/**
 * Score for a single rubric category.
 */
export interface CategoryScore {
  category: string;
  display_name: string;
  average_score?: number | null;
  count?: number;
}

/**
 * Aggregated statistics for a user's call analyses.
 */
export interface CallAnalysisSummary {
  total_calls?: number;
  average_overall_score?: number | null;
  average_talk_time_percentage?: number | null;
  current_tier?: string | null;
  category_scores?: CategoryScore[];
  focus_areas?: string[];
  from_date?: string | null;
  to_date?: string | null;
}

export type ChatMessageRequestRole =
  (typeof ChatMessageRequestRole)[keyof typeof ChatMessageRequestRole];

export const ChatMessageRequestRole = {
  user: "user",
  assistant: "assistant",
} as const;

export type ChatMessageRequestKbToolPayloads = { [key: string]: { [key: string]: unknown } } | null;

/**
 * A single message in the conversation history.
 */
export interface ChatMessageRequest {
  role: ChatMessageRequestRole;
  content: string;
  kb_tool_payloads?: ChatMessageRequestKbToolPayloads;
}

/**
 * Represents a file uploaded by the user for chat context.
 */
export interface UploadedFile {
  /** The original name of the file. */
  name: string;
  /** The MIME type of the file. */
  type: string;
  /** The size of the file in bytes. */
  size: number;
  /** The unique URI returned by the Gemini Files API. */
  gemini_uri: string;
}

/**
 * Request to send a chat message.
 */
export interface ChatRequest {
  /**
   * @minLength 1
   * @maxLength 100000
   */
  message: string;
  conversation_id?: string | null;
  transcript_id?: string | null;
  /** @maxItems 50 */
  history?: ChatMessageRequest[];
  /** A list of files attached to this message (max 10). */
  files?: UploadedFile[] | null;
  rag_enabled?: boolean;
  rag_category_filter?: string | null;
  coaching_role_id?: string | null;
}

/**
 * Response schema for chat settings.
 */
export interface ChatSettingsResponse {
  identity_context_enabled?: boolean;
  updated_at?: string | null;
  updated_by?: string | null;
}

/**
 * Request schema for updating chat settings.
 */
export interface ChatSettingsUpdate {
  identity_context_enabled?: boolean | null;
}

/**
 * Icon type for coaching roles.
 */
export type IconType = (typeof IconType)[keyof typeof IconType];

export const IconType = {
  material: "material",
  custom: "custom",
} as const;

/**
 * Ownership type for coaching roles.
 */
export type OwnershipType = (typeof OwnershipType)[keyof typeof OwnershipType];

export const OwnershipType = {
  internal: "internal",
  external: "external",
} as const;

/**
 * Type of coaching role determining behavior and UI constraints.
 *
 * - STANDARD: Normal admin-configurable role with prompt and personalization
 * - BARE_LLM: Direct model access with no system prompt or personalization
 * - CALL_GRADER: Fixed call grading role (only one can exist)
 */
export type RoleType = (typeof RoleType)[keyof typeof RoleType];

export const RoleType = {
  standard: "standard",
  bare_llm: "bare_llm",
  call_grader: "call_grader",
} as const;

/**
 * Full coaching role info for admins.
 */
export interface CoachingRoleAdmin {
  id: string;
  /** @maxLength 100 */
  name: string;
  /** @maxLength 500 */
  description: string;
  /** @maxLength 16000 */
  system_prompt: string;
  intro_message?: string | null;
  resource_ids?: string[];
  conversation_starters?: string[];
  icon_type: IconType;
  icon_value: string;
  ownership_type: OwnershipType;
  partner_name?: string | null;
  enabled?: boolean;
  display_order: number;
  is_default?: boolean;
  department_access?: Department[] | null;
  rag_enabled?: boolean;
  rag_include_conversations?: boolean;
  rag_min_relevance_score?: number | null;
  rag_source_display_threshold?: number | null;
  include_identity_context?: boolean | null;
  include_goals_detail?: boolean | null;
  include_training_summary?: boolean | null;
  include_resources_summary?: boolean | null;
  include_team_context?: boolean | null;
  is_call_grader?: boolean;
  is_builtin?: boolean;
  role_type?: RoleType;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

/**
 * Coaching role summary for admin listing (no prompt content).
 */
export interface CoachingRoleAdminSummary {
  id: string;
  name: string;
  description: string;
  icon_type: IconType;
  icon_value: string;
  ownership_type: OwnershipType;
  partner_name?: string | null;
  enabled: boolean;
  display_order: number;
  is_default: boolean;
  department_access?: Department[] | null;
  resource_count: number;
  is_builtin?: boolean;
  role_type?: RoleType;
  created_at: string;
  updated_at: string;
}

/**
 * List of all coaching roles for admin.
 */
export interface CoachingRoleAdminListResponse {
  roles: CoachingRoleAdminSummary[];
}

/**
 * Coaching role info visible to users (no system prompt).
 */
export interface CoachingRolePublic {
  id: string;
  /** @maxLength 100 */
  name: string;
  /** @maxLength 500 */
  description: string;
  icon_type: IconType;
  icon_value: string;
  intro_message?: string | null;
  conversation_starters?: string[];
  is_default?: boolean;
  partner_attribution?: string | null;
  department_access?: Department[] | null;
  is_builtin?: boolean;
  role_type?: RoleType;
}

/**
 * List of coaching roles available to user.
 */
export interface CoachingRoleListResponse {
  roles: CoachingRolePublic[];
  default_role_id?: string | null;
}

/**
 * Usage statistics grouped by role.
 */
export interface CoachingUsageByRole {
  role_id: string;
  role_name: string;
  session_count: number;
  message_count: number;
  unique_users: number;
}

/**
 * Usage statistics grouped by user.
 */
export interface CoachingUsageByUser {
  user_id: string;
  user_name: string;
  session_count: number;
  message_count: number;
  most_used_role?: string | null;
}

/**
 * Time period for usage report.
 */
export interface CoachingUsagePeriod {
  start: string;
  end: string;
}

/**
 * Summary statistics for coaching usage.
 */
export interface CoachingUsageSummary {
  total_sessions: number;
  total_messages: number;
  unique_users: number;
  average_session_duration_minutes?: number | null;
}

/**
 * Full coaching usage report.
 */
export interface CoachingUsageReportResponse {
  period: CoachingUsagePeriod;
  summary: CoachingUsageSummary;
  by_role: CoachingUsageByRole[];
  by_user: CoachingUsageByUser[];
}

/**
 * Response for GET /community/settings.
 */
export interface CommunitySettingsResponse {
  curator_feed_id?: string | null;
  updated_at?: string | null;
}

/**
 * Request for PUT /community/settings.
 */
export interface CommunitySettingsUpdateRequest {
  /** Curator.io Feed ID */
  curator_feed_id?: string | null;
}

/**
 * A company event from the organization calendar.
 */
export interface CompanyEvent {
  /** Event ID */
  id: string;
  /** Event title */
  title: string;
  /** Event description */
  description?: string | null;
  /** Event start */
  start_time: string;
  /** Event end */
  end_time: string;
  /** Event location */
  location?: string | null;
  /** Online event */
  is_online?: boolean;
  /** Organizer name */
  organizer_name?: string | null;
}

/**
 * Response for listing company events.
 */
export interface CompanyEventsResponse {
  events: CompanyEvent[];
  total: number;
}

/**
 * Task completion approval status.
 */
export type CompletionStatus = (typeof CompletionStatus)[keyof typeof CompletionStatus];

export const CompletionStatus = {
  not_started: "not_started",
  in_progress: "in_progress",
  pending_approval: "pending_approval",
  approved: "approved",
  rejected: "rejected",
} as const;

/**
 * Task completion verification type.
 */
export type CompletionType = (typeof CompletionType)[keyof typeof CompletionType];

export const CompletionType = {
  self: "self",
  manager_verified: "manager_verified",
  api_linked: "api_linked",
} as const;

/**
 * Response from upload confirmation and validation.
 */
export interface ConfirmUploadResponse {
  /** Whether the photo is valid */
  valid: boolean;
  /** Whether a face was detected */
  face_detected: boolean;
  /** Error message if validation failed */
  error?: string | null;
}

export type ConversationIdentityContext = { [key: string]: unknown } | null;

/**
 * A chat conversation.
 */
export interface Conversation {
  id: string;
  user_id: string;
  /** @maxLength 100 */
  title: string;
  created_at: string;
  updated_at: string;
  message_count?: number;
  coaching_role_id?: string | null;
  coaching_role_name?: string | null;
  identity_context?: ConversationIdentityContext;
}

/**
 * A single Azure Retail Prices row persisted on a message.
 */
export interface ConversationAzurePrice {
  product_name: string;
  sku_name?: string;
  meter_name?: string;
  region?: string;
  retail_price: number;
  unit_of_measure?: string;
  currency_code?: string;
  price_type?: string;
  effective_start_date?: string | null;
}

/**
 * Azure Retail Prices result set persisted on a message.
 */
export interface ConversationAzurePricing {
  filter?: string;
  service_name: string;
  region?: string | null;
  sku_name?: string | null;
  price_type?: string | null;
  results?: ConversationAzurePrice[];
  cached?: boolean;
}

/**
 * A document generated within a conversation message.
 */
export interface ConversationDocument {
  gcs_path: string;
  title: string;
  format: string;
  filename: string;
  mime_type: string;
  file_size?: number;
  url?: string | null;
  expires_at?: string | null;
}

/**
 * An image generated within a conversation message.
 */
export interface ConversationImage {
  gcs_path: string;
  prompt: string;
  url?: string | null;
  expires_at?: string | null;
}

/**
 * Paginated conversation list response.
 */
export interface ConversationListResponse {
  conversations: Conversation[];
  next_cursor?: string | null;
}

/**
 * A single M365 roadmap entry persisted on a message.
 */
export interface ConversationM365RoadmapEntry {
  id: string;
  title: string;
  description?: string;
  status?: string;
  tags?: string[];
  ga_date?: string | null;
  public_preview_date?: string | null;
  url?: string | null;
}

/**
 * M365 roadmap result set persisted on a message.
 */
export interface ConversationM365Roadmap {
  query: string;
  total_matched?: number;
  entries?: ConversationM365RoadmapEntry[];
  cached?: boolean;
}

/**
 * Role of a message sender in a conversation.
 */
export type MessageRole = (typeof MessageRole)[keyof typeof MessageRole];

export const MessageRole = {
  user: "user",
  assistant: "assistant",
} as const;

/**
 * A YouTube recommendation persisted on a conversation message (spec 068).
 *
 * Path A fields (`path_a_score`, `path_a_rationale`) are populated by
 * spec 069's inline content-aware verifier on the top candidate, so
 * reopening the conversation shows the verified badge persistently.
 */
export interface ConversationYouTubeVideo {
  video_id: string;
  url: string;
  title: string;
  channel: string;
  channel_id?: string;
  duration_seconds?: number | null;
  published_at?: string;
  thumbnail_url: string;
  view_count?: number | null;
  description?: string;
  relevance_score?: number | null;
  rationale?: string | null;
  path_a_score?: number | null;
  path_a_rationale?: string | null;
}

/**
 * A single Microsoft Learn doc chunk persisted on a message.
 */
export interface ConversationMicrosoftLearnDoc {
  title: string;
  url: string;
  excerpt?: string;
}

/**
 * Microsoft Learn result set persisted on a message.
 */
export interface ConversationMicrosoftLearn {
  query: string;
  results?: ConversationMicrosoftLearnDoc[];
  cached?: boolean;
}

/**
 * A single MSRC advisory persisted on a message.
 */
export interface ConversationMsrcAdvisory {
  cve_id?: string | null;
  kb_ids?: string[];
  title: string;
  release_date?: string | null;
  severity?: string | null;
  cvss_score?: number | null;
  affected_products?: string[];
  url?: string | null;
}

/**
 * MSRC Security Update Guide result set persisted on a message.
 */
export interface ConversationMsrcSecurity {
  query: string;
  advisories?: ConversationMsrcAdvisory[];
  cached?: boolean;
}

/**
 * Merged CISA KEV + NIST NVD record persisted on a message.
 */
export interface ConversationVulnerabilityRecord {
  cve_id: string;
  description?: string | null;
  cvss_v3_score?: number | null;
  cvss_v3_severity?: string | null;
  published_date?: string | null;
  last_modified_date?: string | null;
  cwe_ids?: string[];
  references?: string[];
  vendor?: string | null;
  product?: string | null;
  kev_listed?: boolean;
  kev_date_added?: string | null;
  kev_due_date?: string | null;
  kev_required_action?: string | null;
}

/**
 * Vulnerability lookup result persisted on a message.
 */
export interface ConversationVulnerability {
  record: ConversationVulnerabilityRecord;
  cached?: boolean;
}

export type MessageFeedbackRating =
  (typeof MessageFeedbackRating)[keyof typeof MessageFeedbackRating];

export const MessageFeedbackRating = {
  up: "up",
  down: "down",
} as const;

/**
 * Spec 093: the owner's rating of an assistant message.
 */
export interface MessageFeedback {
  rating: MessageFeedbackRating;
  comment?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * A message within a conversation.
 */
export interface ConversationMessage {
  id: string;
  role: MessageRole;
  content: string;
  created_at: string;
  files?: UploadedFile[] | null;
  images?: ConversationImage[] | null;
  documents?: ConversationDocument[] | null;
  youtube_videos?: ConversationYouTubeVideo[] | null;
  microsoft_learn?: ConversationMicrosoftLearn | null;
  msrc_security?: ConversationMsrcSecurity | null;
  vulnerability?: ConversationVulnerability | null;
  m365_roadmap?: ConversationM365Roadmap | null;
  azure_pricing?: ConversationAzurePricing | null;
  interrupted?: boolean | null;
  feedback?: MessageFeedback | null;
}

export type ConversationWithMessagesIdentityContext = { [key: string]: unknown } | null;

/**
 * Conversation with full message history.
 */
export interface ConversationWithMessages {
  id: string;
  user_id: string;
  /** @maxLength 100 */
  title: string;
  created_at: string;
  updated_at: string;
  message_count?: number;
  coaching_role_id?: string | null;
  coaching_role_name?: string | null;
  identity_context?: ConversationWithMessagesIdentityContext;
  messages?: ConversationMessage[];
}

/**
 * Request to create a coaching role.
 */
export interface CreateCoachingRoleRequest {
  /**
   * @minLength 1
   * @maxLength 100
   */
  name: string;
  /**
   * @minLength 1
   * @maxLength 500
   */
  description: string;
  /**
   * @minLength 1
   * @maxLength 16000
   */
  system_prompt: string;
  intro_message?: string | null;
  resource_ids?: string[];
  /** @maxItems 5 */
  conversation_starters?: string[];
  icon_type?: IconType;
  icon_value?: string;
  ownership_type?: OwnershipType;
  partner_name?: string | null;
  enabled?: boolean;
  is_default?: boolean;
  department_access?: Department[] | null;
  rag_enabled?: boolean;
  rag_include_conversations?: boolean;
  rag_min_relevance_score?: number | null;
  rag_source_display_threshold?: number | null;
  include_identity_context?: boolean | null;
  include_goals_detail?: boolean | null;
  include_training_summary?: boolean | null;
  include_resources_summary?: boolean | null;
  include_team_context?: boolean | null;
}

/**
 * Request to create a new conversation.
 */
export interface CreateConversationRequest {
  /** @maxLength 100 */
  title?: string;
}

/**
 * Categories for goals.
 */
export type GoalCategory = (typeof GoalCategory)[keyof typeof GoalCategory];

export const GoalCategory = {
  development: "development",
  performance: "performance",
  training: "training",
} as const;

/**
 * Request to create a new goal.
 */
export interface CreateGoalRequest {
  /**
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  description?: string | null;
  category: GoalCategory;
  target_date: string;
  assignee_id?: string | null;
}

/**
 * Input for creating a milestone when creating a goal.
 */
export interface MilestoneInput {
  /**
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  description?: string | null;
  target_date?: string | null;
  /**
   * @minimum 0.1
   * @maximum 10
   */
  weight?: number;
}

/**
 * Request to create a goal with milestones in one call.
 */
export interface CreateGoalWithMilestonesRequest {
  /**
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  description?: string | null;
  category: GoalCategory;
  target_date: string;
  assignee_id?: string | null;
  milestones?: MilestoneInput[] | null;
}

/**
 * Response for create operations that return an ID.
 */
export interface CreateIdResponse {
  id: string;
}

/**
 * Request to create a milestone.
 */
export interface CreateMilestoneRequest {
  /**
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  description?: string | null;
  target_date?: string | null;
  /**
   * @minimum 0.1
   * @maximum 10
   */
  weight?: number;
  order?: number | null;
}

/**
 * Request to create a training requirement.
 */
export interface CreateRequirementRequest {
  course_id: number;
  roles?: string[];
  departments?: string[];
  required?: boolean;
  due_days_from_start?: number | null;
}

/**
 * Request to create a new resource category.
 */
export interface CreateResourceCategoryRequest {
  /**
   * Display name
   * @minLength 1
   * @maxLength 50
   */
  name: string;
  /** Help text for the category */
  description?: string | null;
  /** Sort order (0-based) */
  display_order?: number | null;
}

/**
 * Request to create a subtask.
 */
export interface CreateSubtaskRequest {
  /**
   * @minLength 1
   * @maxLength 200
   */
  title: string;
}

/**
 * Priority level for tasks.
 */
export type TaskPriority = (typeof TaskPriority)[keyof typeof TaskPriority];

export const TaskPriority = {
  low: "low",
  medium: "medium",
  high: "high",
} as const;

/**
 * Input for creating or updating a subtask.
 */
export interface SubtaskInput {
  /**
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  completed?: boolean;
}

/**
 * Request to create a task.
 */
export interface CreateTaskRequest {
  /**
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  description?: string | null;
  priority?: TaskPriority;
  due_date?: string | null;
  linked_course_id?: number | null;
  subtasks?: SubtaskInput[] | null;
}

/**
 * Request to create a template.
 */
export interface CreateTemplateRequest {
  /** @maxLength 100 */
  name: string;
  /** @maxLength 500 */
  description: string;
  /** @pattern ^(pdf|docx|xlsx)$ */
  format: string;
  data_sources?: string[];
  /** @maxLength 2000 */
  prompt: string;
  /** @pattern ^(prompt_based|file_based)$ */
  template_type?: string;
}

/**
 * Task creation approval status.
 */
export type CreationStatus = (typeof CreationStatus)[keyof typeof CreationStatus];

export const CreationStatus = {
  active: "active",
  pending_approval: "pending_approval",
  rejected: "rejected",
} as const;

/**
 * Status of a milestone.
 */
export type MilestoneStatus = (typeof MilestoneStatus)[keyof typeof MilestoneStatus];

export const MilestoneStatus = {
  pending: "pending",
  in_progress: "in_progress",
  completed: "completed",
} as const;

/**
 * Milestone summary for dashboard display.
 */
export interface UpcomingMilestone {
  id: string;
  title: string;
  goal_id: string;
  goal_title: string;
  target_date: string;
  progress: number;
  status: MilestoneStatus;
  is_overdue: boolean;
}

/**
 * Dashboard summary with upcoming milestones.
 */
export interface DashboardSummaryResponse {
  upcoming_milestones: UpcomingMilestone[];
  total_active_goals: number;
  total_active_milestones: number;
  overdue_milestone_count: number;
}

/**
 * Date range options for report filtering.
 */
export type DateRange = (typeof DateRange)[keyof typeof DateRange];

export const DateRange = {
  "30d": "30d",
  "90d": "90d",
  "6mo": "6mo",
  "1yr": "1yr",
  all: "all",
} as const;

/**
 * Response from deleting avatar.
 */
export interface DeleteAvatarResponse {
  success: boolean;
}

/**
 * Response after deleting a category.
 */
export interface DeleteCategoryResponse {
  /** Whether deletion was successful */
  deleted: boolean;
  /** Number of resources reassigned to uncategorized */
  resources_reassigned: number;
}

/**
 * Standard response for delete operations.
 */
export interface DeleteResponse {
  success?: boolean;
  message?: string | null;
}

/**
 * Response after deleting a transcript.
 */
export interface DeleteTranscriptResponse {
  message: string;
  deleted_id: string;
}

/**
 * Coaching role assignment for a department.
 */
export interface DepartmentAssignment {
  department: Department;
  coaching_role_ids: string[];
  default_role_id?: string | null;
}

/**
 * List of all department assignments.
 */
export interface DepartmentAssignmentListResponse {
  assignments: DepartmentAssignment[];
}

/**
 * User count breakdown by department.
 */
export interface DepartmentBreakdown {
  sales?: number;
  service?: number;
  operations?: number;
  unassigned?: number;
}

/**
 * Response schema for document generation admin settings.
 */
export interface DocumentGenerationSettingsResponse {
  enabled?: boolean;
  /**
   * @minimum 1
   * @maximum 100
   */
  rate_limit_per_hour?: number;
  updated_at?: string | null;
  updated_by?: string | null;
}

/**
 * Request schema for updating document generation settings.
 */
export interface DocumentGenerationSettingsUpdate {
  enabled?: boolean | null;
  rate_limit_per_hour?: number | null;
}

/**
 * Response containing signed download URL.
 */
export interface DownloadUrlResponse {
  /** Signed URL for downloading the file */
  download_url: string;
  /** Original filename */
  filename: string;
  /** When the signed URL expires */
  expires_at: string;
}

/**
 * Response for the Engagement tab.
 */
export interface EngagementResponse {
  engagement_score: number;
  engagement_trend: MetricTrend;
  active_users: number;
  total_users: number;
  engagement_history: TimeSeriesPoint[];
}

/**
 * Lifecycle of a KB item's kind-specific enrichment job.
 */
export type EnrichmentStatus = (typeof EnrichmentStatus)[keyof typeof EnrichmentStatus];

export const EnrichmentStatus = {
  pending: "pending",
  enriching: "enriching",
  ready: "ready",
  failed: "failed",
} as const;

/**
 * Standard error response returned by all endpoints.
 */
export interface ErrorResponse {
  detail: string;
}

/**
 * Export format options.
 */
export type ExportFormat = (typeof ExportFormat)[keyof typeof ExportFormat];

export const ExportFormat = {
  csv: "csv",
  pdf: "pdf",
} as const;

/**
 * Request to connect Fireflies account.
 */
export interface FirefliesConnectRequest {
  /**
   * @minLength 10
   * @maxLength 200
   */
  api_key: string;
}

/**
 * Response after disconnecting Fireflies.
 */
export interface FirefliesDisconnectResponse {
  message: string;
}

/**
 * Status of an integration connection.
 */
export type IntegrationStatus = (typeof IntegrationStatus)[keyof typeof IntegrationStatus];

export const IntegrationStatus = {
  connected: "connected",
  error: "error",
  not_connected: "not_connected",
} as const;

/**
 * Response with Fireflies connection status.
 */
export interface FirefliesStatusResponse {
  connected: boolean;
  connected_at?: string | null;
  last_validated?: string | null;
  status: IntegrationStatus;
  error_message?: string | null;
}

/**
 * Summary of a transcript from Fireflies API (for list view).
 */
export interface FirefliesTranscriptSummary {
  id: string;
  title: string;
  date: string;
  duration: number;
  participant_count: number;
  has_summary: boolean;
}

/**
 * Response with list of Fireflies transcripts.
 */
export interface FirefliesTranscriptListResponse {
  transcripts: FirefliesTranscriptSummary[];
  total: number;
  has_more: boolean;
}

/**
 * Request to start avatar generation.
 */
export interface GenerateAvatarRequest {
  /** Target career level for the future self */
  target_career_level?: CareerLevel;
  /**
   * Years into the future (5, 10, 15, or 20)
   * @minimum 5
   * @maximum 20
   */
  years_into_future?: number;
  /** Optional additional context for generation */
  custom_context?: string | null;
}

/**
 * Response from starting avatar generation.
 */
export interface GenerateAvatarResponse {
  /** Avatar record ID */
  avatar_id: string;
  /** Current status (generating) */
  status: AvatarStatus;
  /** Estimated time to complete generation */
  estimated_time_seconds: number;
}

/**
 * Status of a goal.
 */
export type GoalStatus = (typeof GoalStatus)[keyof typeof GoalStatus];

export const GoalStatus = {
  active: "active",
  completed: "completed",
  archived: "archived",
} as const;

/**
 * Status of a task.
 */
export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

export const TaskStatus = {
  pending: "pending",
  completed: "completed",
} as const;

/**
 * Subtask response.
 */
export interface SubtaskResponse {
  id: string;
  title: string;
  completed: boolean;
  completed_at?: string | null;
}

export type TaskResponseApiVerificationData = { [key: string]: unknown } | null;

/**
 * Task response.
 */
export interface TaskResponse {
  id: string;
  milestone_id: string;
  goal_id: string;
  title: string;
  description: string | null;
  order: number;
  priority: TaskPriority;
  status: TaskStatus;
  due_date: string | null;
  completed_at: string | null;
  linked_course_id: number | null;
  linked_course_name: string | null;
  linked_course_slug?: string | null;
  subtasks: SubtaskResponse[];
  created_at: string;
  updated_at: string;
  created_by_id?: string | null;
  created_by_name?: string | null;
  completion_type?: CompletionType;
  creation_status?: CreationStatus;
  creation_approved_by_id?: string | null;
  creation_approved_at?: string | null;
  creation_rejection_reason?: string | null;
  completion_status?: CompletionStatus;
  completion_submitted_at?: string | null;
  completion_approved_by_id?: string | null;
  completion_approved_by_name?: string | null;
  completion_approved_at?: string | null;
  completion_feedback?: string | null;
  api_verified_at?: string | null;
  api_verification_data?: TaskResponseApiVerificationData;
  requirement_id?: string | null;
}

/**
 * Milestone response.
 */
export interface MilestoneResponse {
  id: string;
  goal_id: string;
  title: string;
  description: string | null;
  order: number;
  weight: number;
  status: MilestoneStatus;
  target_date: string | null;
  completed_at: string | null;
  progress: number;
  task_count: number;
  completed_task_count: number;
  tasks?: TaskResponse[] | null;
  created_at: string;
  updated_at: string;
  creation_status?: CreationStatus;
  creation_approved_by_id?: string | null;
  creation_approved_at?: string | null;
  creation_rejection_reason?: string | null;
}

/**
 * Goal response.
 */
export interface GoalResponse {
  id: string;
  title: string;
  description: string | null;
  category: GoalCategory;
  user_id: string;
  user_name: string;
  user_email: string;
  assigned_by_id: string | null;
  assigned_by_name: string | null;
  status: GoalStatus;
  target_date: string;
  completed_at: string | null;
  progress: number;
  milestone_count: number;
  completed_milestone_count: number;
  milestones?: MilestoneResponse[] | null;
  created_at: string;
  updated_at: string;
  approval_required?: boolean;
  creation_status?: CreationStatus;
  creation_submitted_at?: string | null;
  creation_approved_by_id?: string | null;
  creation_approved_by_name?: string | null;
  creation_approved_at?: string | null;
  creation_rejection_reason?: string | null;
}

/**
 * Response for listing goals.
 */
export interface GoalListResponse {
  goals: GoalResponse[];
  total: number;
  active_count: number;
  completed_count: number;
}

/**
 * Statistics about user's goals.
 */
export interface GoalStatsResponse {
  total_goals: number;
  active_goals: number;
  completed_goals: number;
  average_progress: number;
  overdue_count: number;
}

export type ValidationErrorCtx = { [key: string]: unknown };

export interface ValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
  input?: unknown;
  ctx?: ValidationErrorCtx;
}

export interface HTTPValidationError {
  detail?: ValidationError[];
}

/**
 * Health check endpoint response.
 */
export interface HealthResponse {
  status: string;
  service: string;
  version: string;
}

/**
 * Body for ``PATCH /admin/integrations/hubspot``.
 */
export interface HubSpotAdminConfigRequest {
  /**
   * @minLength 1
   * @maxLength 200
   */
  client_id: string;
  /**
   * @minLength 1
   * @maxLength 200
   */
  client_secret: string;
}

/**
 * Response for ``GET /admin/integrations/hubspot``.
 */
export interface HubSpotAdminStatus {
  configured: boolean;
  connected_user_count?: number;
  configured_at?: string | null;
  configured_by?: string | null;
}

/**
 * Response for ``POST /integrations/hubspot/connect``.
 */
export interface HubSpotConnectResponse {
  auth_url: string;
}

/**
 * Response for ``GET /integrations/hubspot/status``.
 */
export interface HubSpotConnectionStatus {
  connected: boolean;
  portal_id?: string | null;
  portal_name?: string | null;
  connected_at?: string | null;
}

export type HubSpotCrmPayloadProperties = { [key: string]: unknown };

/**
 * Kind-specific payload for a HubSpot CRM record (spec 072).
 */
export interface HubSpotCrmPayload {
  kind?: "hubspot_crm";
  /** @maxLength 32 */
  object_type: string;
  /** @maxLength 64 */
  object_id: string;
  /** @maxLength 32 */
  portal_id: string;
  properties?: HubSpotCrmPayloadProperties;
  snapshot_taken_at?: string | null;
}

/**
 * Response for icon upload operations.
 */
export interface IconUrlResponse {
  icon_url: string;
}

/**
 * Response schema for image generation admin settings.
 */
export interface ImageGenerationSettingsResponse {
  enabled?: boolean;
  /**
   * @minimum 1
   * @maximum 100
   */
  rate_limit_per_hour?: number;
  updated_at?: string | null;
  updated_by?: string | null;
}

/**
 * Request schema for updating image generation settings.
 */
export interface ImageGenerationSettingsUpdate {
  enabled?: boolean | null;
  rate_limit_per_hour?: number | null;
}

/**
 * Speaker information from transcript.
 */
export interface TranscriptSpeaker {
  id: string;
  name: string;
}

/**
 * A sentence from the transcript with speaker info.
 */
export interface TranscriptSentence {
  speaker_id?: string | null;
  speaker_name?: string | null;
  text: string;
  start_time?: number | null;
  end_time?: number | null;
}

/**
 * AI-generated summary from Fireflies.
 */
export interface TranscriptSummary {
  keywords?: string[];
  action_items?: string[];
  overview?: string | null;
  outline?: string | null;
  shorthand_bullet?: string | null;
}

/**
 * Full imported transcript stored locally.
 */
export interface ImportedTranscript {
  id: string;
  fireflies_id: string;
  title: string;
  date: string;
  duration: number;
  participants: string[];
  speakers: TranscriptSpeaker[];
  transcript_text: string;
  sentences: TranscriptSentence[];
  summary?: TranscriptSummary | null;
  imported_at: string;
  submitted_to_coach?: boolean;
  last_coached_at?: string | null;
}

/**
 * Response after importing a transcript.
 */
export interface ImportTranscriptResponse {
  transcript: ImportedTranscript;
  message: string;
}

/**
 * Category for imported meetings.
 */
export type MeetingCategory = (typeof MeetingCategory)[keyof typeof MeetingCategory];

export const MeetingCategory = {
  one_on_one: "one_on_one",
  coaching: "coaching",
  training: "training",
  team: "team",
  other: "other",
} as const;

/**
 * A meeting imported from Outlook.
 */
export interface ImportedMeeting {
  /** Document ID */
  id: string;
  /** Microsoft Graph event ID */
  outlook_event_id: string;
  /** Meeting title */
  subject: string;
  /** Meeting start */
  start_time: string;
  /** Meeting end */
  end_time: string;
  /** Organizer email */
  organizer_email?: string | null;
  /** Organizer name */
  organizer_name?: string | null;
  /** Attendee emails */
  attendees?: string[];
  /** Meeting location */
  location?: string | null;
  /** Teams/online meeting */
  is_online?: boolean;
  /** Meeting category */
  category?: MeetingCategory;
  /** When imported */
  imported_at: string;
}

/**
 * Summary of imported transcript for list view.
 */
export interface ImportedTranscriptSummary {
  id: string;
  fireflies_id: string;
  title: string;
  date: string;
  duration: number;
  participant_count: number;
  imported_at: string;
  submitted_to_coach: boolean;
}

/**
 * The kind of content a ``KbItem`` represents.
 */
export type KbItemKind = (typeof KbItemKind)[keyof typeof KbItemKind];

export const KbItemKind = {
  youtube_video: "youtube_video",
  ms_learn_doc: "ms_learn_doc",
  vulnerability: "vulnerability",
  roadmap_item: "roadmap_item",
  pricing_snapshot: "pricing_snapshot",
  url: "url",
  hubspot_crm: "hubspot_crm",
  autotask: "autotask",
} as const;

/**
 * Cloud Tasks payload POSTed to ``/internal/kb/enrich/{kind}``.
 *
 * The payload intentionally holds only identity so the worker reads
 * the full ``KbItem`` from Firestore at dispatch time — this keeps
 * the Cloud Tasks body well under the 100 KB limit and avoids
 * stale-payload hazards on retries.
 */
export interface KbEnrichmentJobRequest {
  /**
   * @minLength 1
   * @maxLength 128
   */
  user_id: string;
  /**
   * @minLength 1
   * @maxLength 128
   */
  item_id: string;
  kind: KbItemKind;
}

/**
 * How a ``KbItem`` was created.
 */
export type SavedVia = (typeof SavedVia)[keyof typeof SavedVia];

export const SavedVia = {
  chat_sources_panel: "chat_sources_panel",
  manual_url: "manual_url",
  migration: "migration",
} as const;

/**
 * Kind-specific payload for a YouTube video saved from the Coach.
 */
export interface YouTubeVideoPayload {
  kind?: "youtube_video";
  /**
   * @minLength 1
   * @maxLength 32
   */
  video_id: string;
  /** @maxLength 200 */
  channel?: string;
  /** @maxLength 64 */
  channel_id?: string;
  duration_seconds?: number | null;
  /** @maxLength 64 */
  published_at?: string;
  path_b_score?: number | null;
  path_b_rationale?: string | null;
  path_a_score?: number | null;
  path_a_rationale?: string | null;
  legacy_verification_status?: string | null;
}

/**
 * Kind-specific payload for a Microsoft Learn article.
 */
export interface MsLearnDocPayload {
  kind?: "ms_learn_doc";
  /** @maxLength 4000 */
  content_chunk?: string;
  /** @maxLength 2000 */
  excerpt?: string;
  summary?: string | null;
  full_text_snippet?: string | null;
}

/**
 * Kind-specific payload for a CVE advisory.
 *
 * One ``KbItem`` per CVE. When the same CVE is saved from both
 * ``msrc_security_lookup`` and ``vulnerability_lookup``, the two
 * adapters merge into the single payload — the MSRC block and the
 * NVD/KEV block coexist, and ``source_contributions`` lists every
 * source that has written to this item.
 */
export interface VulnerabilityPayload {
  kind?: "vulnerability";
  /**
   * @minLength 1
   * @maxLength 32
   */
  cve_id: string;
  cvss_v3_score?: number | null;
  cvss_v3_severity?: string | null;
  /** @maxItems 20 */
  cwe_ids?: string[];
  kev_listed?: boolean;
  kev_date_added?: string | null;
  kev_due_date?: string | null;
  kev_required_action?: string | null;
  msrc_advisory_title?: string | null;
  msrc_release_date?: string | null;
  /** @maxItems 50 */
  msrc_affected_products?: string[];
  /** @maxItems 20 */
  msrc_kb_numbers?: string[];
  snapshot_taken_at?: string | null;
  /** @maxItems 10 */
  source_contributions?: string[];
}

/**
 * Kind-specific payload for a Microsoft 365 Roadmap entry.
 */
export interface RoadmapItemPayload {
  kind?: "roadmap_item";
  /**
   * @minLength 1
   * @maxLength 32
   */
  roadmap_id: string;
  /** @maxLength 64 */
  status?: string;
  /** @maxLength 64 */
  status_at_save?: string;
  /** @maxItems 20 */
  tags?: string[];
  ga_date?: string | null;
  snapshot_taken_at?: string | null;
}

/**
 * Kind-specific payload for an Azure retail-price snapshot.
 */
export interface PricingSnapshotPayload {
  kind?: "pricing_snapshot";
  /**
   * @minLength 1
   * @maxLength 200
   */
  product_name: string;
  sku_name?: string | null;
  /**
   * @minLength 1
   * @maxLength 100
   */
  service_name: string;
  meter_name?: string | null;
  /**
   * @minLength 1
   * @maxLength 50
   */
  region: string;
  /** @minimum 0 */
  retail_price: number;
  /** @maxLength 8 */
  currency_code?: string;
  /** @maxLength 50 */
  unit_of_measure?: string;
  /** @maxLength 32 */
  price_type?: string;
  /** @maxLength 32 */
  effective_start_date?: string;
  snapshot_taken_at?: string | null;
}

/**
 * Kind-specific payload for a manually-saved URL.
 */
export interface UrlPayload {
  kind?: "url";
  favicon_url?: string | null;
  site_name?: string | null;
  readability_extract?: string | null;
}

/**
 * A single knowledgebase item, persisted at ``user_kb/{uid}/items/{item_id}``.
 */
export interface KbItem {
  /**
   * @minLength 1
   * @maxLength 128
   */
  item_id: string;
  kind: KbItemKind;
  /**
   * @minLength 1
   * @maxLength 500
   */
  title: string;
  /** @maxLength 2000 */
  description?: string;
  /**
   * @minLength 1
   * @maxLength 2083
   */
  source_url: string;
  thumbnail_url?: string | null;
  icon?: string | null;
  /**
   * @minLength 1
   * @maxLength 64
   */
  source_tool: string;
  source_query?: string | null;
  source_conversation_id?: string | null;
  saved_via: SavedVia;
  saved_at: string;
  payload:
    | YouTubeVideoPayload
    | MsLearnDocPayload
    | VulnerabilityPayload
    | RoadmapItemPayload
    | PricingSnapshotPayload
    | UrlPayload
    | HubSpotCrmPayload
    | AutoTaskPayload;
  enrichment_status?: EnrichmentStatus;
  enrichment_job_id?: string | null;
  /** @minimum 0 */
  enrichment_attempts?: number;
  enrichment_last_error?: string | null;
  enriched_at?: string | null;
  archived?: boolean;
  read_at?: string | null;
  pinned?: boolean;
  promoted_resource_id?: string | null;
  /** @maxItems 20 */
  tags?: string[];
  created_at: string;
  updated_at: string;
}

/**
 * Paginated list response for ``GET /api/v1/kb/items``.
 */
export interface KbItemListResponse {
  items: KbItem[];
  next_cursor?: string | null;
  /** @minimum 0 */
  total?: number;
}

/**
 * Body of ``PATCH /api/v1/kb/items/{item_id}`` — all fields optional.
 */
export interface KbItemPatchRequest {
  read?: boolean | null;
  archived?: boolean | null;
  pinned?: boolean | null;
  title?: string | null;
  description?: string | null;
  tags?: string[] | null;
}

export type KbItemsExistsResponseSaved = { [key: string]: boolean };

/**
 * Response body for ``GET /api/v1/kb/items/exists``.
 */
export interface KbItemsExistsResponse {
  saved: KbItemsExistsResponseSaved;
}

/**
 * Body of ``POST /api/v1/kb/items/manual``.
 */
export interface KbManualSaveRequest {
  /**
   * @minLength 1
   * @maxLength 2083
   */
  source_url: string;
  title?: string | null;
  description?: string | null;
  /** @maxItems 10 */
  tags?: string[];
}

/**
 * Body of ``POST /api/v1/kb/items/{item_id}/promote``.
 */
export interface KbPromoteRequest {
  /**
   * @minLength 1
   * @maxLength 50
   */
  category?: string;
  /** @maxItems 10 */
  tags?: string[];
  /** @maxLength 32 */
  visibility?: string;
  description_override?: string | null;
}

/**
 * Response body for ``POST /items/{item_id}/promote``.
 */
export interface KbPromoteResponse {
  resource_id: string;
  already_promoted: boolean;
}

export type KbSaveFromAgentRequestRawResult = { [key: string]: unknown };

/**
 * Body of ``POST /api/v1/kb/items/from-agent``.
 */
export interface KbSaveFromAgentRequest {
  /**
   * @minLength 1
   * @maxLength 128
   */
  conversation_id: string;
  /**
   * @minLength 1
   * @maxLength 64
   */
  tool_name: string;
  /**
   * @minimum 0
   * @maximum 100
   */
  result_index: number;
  raw_result: KbSaveFromAgentRequestRawResult;
}

/**
 * Response body for save endpoints.
 *
 * ``was_already_saved`` lets the Sources panel show a "Saved ✓" hint
 * without changing HTTP semantics — the server returns 201 on a new
 * save and 200 on an idempotent re-save, and the UI keys off this
 * flag for toast copy.
 */
export interface KbSaveResponse {
  item: KbItem;
  was_already_saved: boolean;
}

/**
 * Response for the Learning Progress tab.
 */
export interface LearningProgressResponse {
  pathways_completed: number;
  pathways_completed_trend: MetricTrend;
  avg_completion_time_days: number;
  avg_completion_time_trend: MetricTrend;
  progress_trend: TimeSeriesPoint[];
}

export interface LogoutResponse {
  message?: string;
}

/**
 * Response for listing imported meetings.
 */
export interface MeetingListResponse {
  meetings: ImportedMeeting[];
  total: number;
}

export type MessageFeedbackRequestRating =
  (typeof MessageFeedbackRequestRating)[keyof typeof MessageFeedbackRequestRating];

export const MessageFeedbackRequestRating = {
  up: "up",
  down: "down",
} as const;

/**
 * Spec 093: set (or change) the rating on an assistant message.
 */
export interface MessageFeedbackRequest {
  rating: MessageFeedbackRequestRating;
  comment?: string | null;
}

/**
 * Response for getting current user's avatar.
 */
export interface MyAvatarResponse {
  /** Whether user has an avatar */
  has_avatar: boolean;
  /** Avatar details if exists */
  avatar?: AvatarResponse | null;
}

/**
 * Information about an available calendar for org selection.
 */
export interface OrgCalendarInfo {
  /** Calendar ID */
  id: string;
  /** Calendar name */
  name: string;
  /** Calendar owner */
  owner?: string | null;
}

/**
 * Status of organization calendar connection.
 */
export interface OrgCalendarStatusResponse {
  enabled: boolean;
  calendar_id?: string | null;
  calendar_name?: string | null;
  connected_by?: string | null;
  connected_at?: string | null;
  last_fetch_at?: string | null;
}

/**
 * Response listing admin's accessible calendars.
 */
export interface OrgCalendarsResponse {
  calendars: OrgCalendarInfo[];
}

/**
 * User's Outlook sync preferences.
 */
export interface OutlookSettings {
  /** Whether calendar sync is enabled */
  enabled?: boolean;
  /** Selected calendar ID (default: primary) */
  calendar_id?: string | null;
  /** Sync goal deadlines */
  sync_goals?: boolean;
  /** Sync task due dates */
  sync_tasks?: boolean;
  /** Sync training time */
  sync_training?: boolean;
  /** Default reminder in minutes */
  reminder_minutes?: number;
}

/**
 * Response after updating settings.
 */
export interface OutlookSettingsResponse {
  success: boolean;
  settings: OutlookSettings;
}

/**
 * Request to update Outlook sync settings.
 */
export interface OutlookSettingsUpdate {
  enabled?: boolean | null;
  calendar_id?: string | null;
  sync_goals?: boolean | null;
  sync_tasks?: boolean | null;
  sync_training?: boolean | null;
  reminder_minutes?: number | null;
}

/**
 * Status of calendar sync operation.
 */
export type OutlookSyncStatus = (typeof OutlookSyncStatus)[keyof typeof OutlookSyncStatus];

export const OutlookSyncStatus = {
  idle: "idle",
  syncing: "syncing",
  error: "error",
} as const;

/**
 * Statistics about synced calendar events.
 */
export interface OutlookSyncStats {
  goals_synced?: number;
  tasks_synced?: number;
  training_synced?: number;
}

/**
 * Response for calendar availability and status.
 */
export interface OutlookStatusResponse {
  /** Whether calendar sync is available for this user */
  available: boolean;
  /** Reason if not available (e.g., not Microsoft user) */
  reason?: string | null;
  /** Whether sync is enabled */
  enabled?: boolean | null;
  /** Last successful sync */
  last_sync_at?: string | null;
  /** Current sync status */
  sync_status?: OutlookSyncStatus | null;
  /** Last error message if any */
  error_message?: string | null;
  /** Current sync settings */
  settings?: OutlookSettings | null;
  /** Sync statistics */
  stats?: OutlookSyncStats | null;
}

/**
 * Result of a sync operation.
 */
export interface OutlookSyncResult {
  goals?: number;
  tasks?: number;
  training?: number;
}

/**
 * Response for manual sync trigger.
 */
export interface OutlookSyncResponse {
  success: boolean;
  synced?: OutlookSyncResult | null;
  errors?: string[];
}

/**
 * File type classifications for resources.
 */
export type ResourceFileType = (typeof ResourceFileType)[keyof typeof ResourceFileType];

export const ResourceFileType = {
  document: "document",
  video: "video",
  audio: "audio",
  spreadsheet: "spreadsheet",
  presentation: "presentation",
  image: "image",
  template: "template",
  link: "link",
  other: "other",
} as const;

/**
 * Resource visibility level.
 */
export type Visibility = (typeof Visibility)[keyof typeof Visibility];

export const Visibility = {
  private: "private",
  team: "team",
  organization: "organization",
} as const;

/**
 * Status of RAG indexing for a resource.
 */
export type RAGStatus = (typeof RAGStatus)[keyof typeof RAGStatus];

export const RAGStatus = {
  pending: "pending",
  processing: "processing",
  indexed: "indexed",
  failed: "failed",
  disabled: "disabled",
} as const;

/**
 * Response representing a resource.
 */
export interface ResourceResponse {
  id: string;
  title: string;
  description: string;
  category: string;
  file_type: ResourceFileType;
  tags: string[];
  filename?: string | null;
  mime_type?: string | null;
  file_size?: number | null;
  download_url?: string | null;
  preview_url?: string | null;
  external_url?: string | null;
  created_by: string;
  created_by_name?: string | null;
  created_at: string;
  updated_by?: string | null;
  updated_at: string;
  current_version?: number;
  view_count?: number;
  is_deleted?: boolean;
  visibility?: Visibility;
  shared_with_team_of?: string | null;
  department_restrictions?: Department[] | null;
  rag_status?: RAGStatus | null;
  rag_chunk_count?: number | null;
  rag_indexed_at?: string | null;
  rag_error?: string | null;
}

/**
 * Paginated list of resources.
 */
export interface PaginatedResources {
  items: ResourceResponse[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

/**
 * Goal pending creation approval.
 */
export interface PendingGoalCreation {
  goal_id: string;
  goal_title: string;
  category: GoalCategory;
  user_id: string;
  user_name: string;
  target_date: string;
  milestone_count: number;
  task_count: number;
  created_at: string;
  creation_submitted_at?: string | null;
}

/**
 * Milestone pending creation approval.
 */
export interface PendingMilestoneCreation {
  milestone_id: string;
  milestone_title: string;
  goal_id: string;
  goal_title: string;
  user_id: string;
  user_name: string;
  created_at: string;
}

/**
 * Summary of a task pending creation approval.
 */
export interface PendingTaskCreation {
  task_id: string;
  task_title: string;
  goal_id: string;
  goal_title: string;
  milestone_id: string;
  milestone_title: string;
  user_id: string;
  user_name: string;
  created_at: string;
  created_by_id: string;
  created_by_name: string | null;
}

/**
 * Summary of a task pending completion approval.
 */
export interface PendingTaskCompletion {
  task_id: string;
  task_title: string;
  goal_id: string;
  goal_title: string;
  milestone_id: string;
  milestone_title: string;
  user_id: string;
  user_name: string;
  submitted_at: string;
  completed_at: string | null;
}

/**
 * Response containing pending approvals for a manager.
 */
export interface PendingApprovalsResponse {
  pending_goal_creations?: PendingGoalCreation[];
  pending_milestone_creations?: PendingMilestoneCreation[];
  pending_creations: PendingTaskCreation[];
  pending_completions: PendingTaskCompletion[];
  goal_creation_count?: number;
  milestone_creation_count?: number;
  creation_count: number;
  completion_count: number;
}

/**
 * API response for RAG system statistics.
 */
export interface RAGStatsResponse {
  total_resources_indexed: number;
  total_chunks: number;
  failed_resources: number;
  pending_resources: number;
  processing_resources: number;
}

/**
 * API response for RAG status of a resource.
 */
export interface RAGStatusResponse {
  status: RAGStatus;
  chunk_count?: number | null;
  indexed_at?: string | null;
  error?: string | null;
}

/**
 * Response for RAG availability status.
 */
export interface RagStatusResponse {
  available: boolean;
}

/**
 * Request to regenerate avatar with same or updated parameters.
 */
export interface RegenerateRequest {
  /** Updated career level (keep existing if None) */
  target_career_level?: CareerLevel | null;
  /** Updated years (keep existing if None) */
  years_into_future?: number | null;
  /** Updated context (keep existing if None) */
  custom_context?: string | null;
}

export interface RegisterDevAccountRequest {
  email: string;
  /** @minLength 8 */
  password: string;
  display_name?: string | null;
  job_title?: string | null;
  role?: UserRole;
}

export interface RegisterDevAccountResponse {
  user: UserProfileResponse;
  message?: string;
}

/**
 * Request to reject a task completion.
 */
export interface RejectCompletionRequest {
  /**
   * @minLength 1
   * @maxLength 500
   */
  feedback: string;
}

/**
 * Request to reject a task creation.
 */
export interface RejectCreationRequest {
  /**
   * @minLength 1
   * @maxLength 500
   */
  reason: string;
}

/**
 * Request to reorder categories.
 */
export interface ReorderCategoriesRequest {
  /**
   * List of category slugs in desired order
   * @minItems 1
   * @maxItems 49
   */
  order: string[];
}

/**
 * Response after reordering categories.
 */
export interface ReorderCategoriesResponse {
  /** Number of categories updated */
  updated: number;
}

/**
 * Request to reorder coaching roles.
 */
export interface ReorderCoachingRolesRequest {
  /** @minItems 1 */
  role_ids: string[];
}

/**
 * Request to reorder milestones.
 */
export interface ReorderMilestonesRequest {
  goal_id: string;
  milestone_ids: string[];
}

/**
 * Response for reorder operations (milestones, tasks).
 */
export interface ReorderResponse {
  success?: boolean;
}

/**
 * Request to reorder tasks within a milestone.
 */
export interface ReorderTasksRequest {
  milestone_id: string;
  task_ids: string[];
}

/**
 * Request to report poor generation quality.
 */
export interface ReportPoorResultRequest {
  /** Optional feedback about the result */
  feedback?: string | null;
}

/**
 * Response from reporting poor result.
 */
export interface ReportPoorResultResponse {
  success: boolean;
  /** Number of additional regenerations granted */
  additional_regenerations_granted: number;
}

/**
 * Preview of users matching requirement criteria.
 */
export interface RequirementPreviewResponse {
  total_count: number;
  sample_users: string[];
}

/**
 * Category response for admin API with additional fields.
 */
export interface ResourceCategoryAdminResponse {
  /** Unique identifier */
  slug: string;
  /** Display name */
  name: string;
  /** Help text */
  description?: string | null;
  /** Sort order */
  display_order: number;
  /** True for system categories like 'uncategorized' */
  is_system: boolean;
  /** Number of resources using this category */
  resource_count?: number;
  /** Creation timestamp */
  created_at: string;
  /** Last update timestamp */
  updated_at: string;
}

/**
 * List response for admin API.
 */
export interface ResourceCategoryAdminListResponse {
  categories: ResourceCategoryAdminResponse[];
  total: number;
}

/**
 * Category response for public API (dropdowns, filters).
 */
export interface ResourceCategoryResponse {
  /** Unique identifier */
  slug: string;
  /** Display name */
  name: string;
  /** Help text */
  description?: string | null;
}

/**
 * List response for public API.
 */
export interface ResourceCategoryListResponse {
  categories: ResourceCategoryResponse[];
}

/**
 * A coaching role associated with a resource.
 */
export interface ResourceCoachingRole {
  id: string;
  name: string;
}

/**
 * Response with coaching roles associated with a resource.
 */
export interface ResourceCoachingRolesResponse {
  resource_id: string;
  coaching_roles: ResourceCoachingRole[];
}

/**
 * Request for creating a new resource after upload.
 */
export interface ResourceCreate {
  /**
   * Resource title
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  /**
   * Resource description
   * @minLength 1
   * @maxLength 2000
   */
  description: string;
  /**
   * Category slug
   * @minLength 1
   * @maxLength 50
   */
  category: string;
  file_type: ResourceFileType;
  /**
   * Tags for organization
   * @maxItems 20
   */
  tags?: string[];
  /** Upload session ID from upload-url */
  upload_id: string;
  /**
   * Original filename
   * @maxLength 255
   */
  filename: string;
  /** MIME type of the file */
  mime_type: string;
  /**
   * File size in bytes
   * @exclusiveMinimum 0
   */
  file_size: number;
  /** Who can see this resource */
  visibility?: Visibility;
  /** Departments that can view this resource (null = all departments) */
  department_restrictions?: Department[] | null;
}

/**
 * Request for creating an external link resource.
 */
export interface ResourceLinkCreate {
  /**
   * Resource title
   * @minLength 1
   * @maxLength 200
   */
  title: string;
  /**
   * Resource description
   * @minLength 1
   * @maxLength 2000
   */
  description: string;
  /**
   * Category slug
   * @minLength 1
   * @maxLength 50
   */
  category: string;
  /**
   * Tags for organization
   * @maxItems 20
   */
  tags?: string[];
  /**
   * External URL for the resource
   * @minLength 1
   * @maxLength 2083
   */
  external_url: string;
  /** Who can see this resource */
  visibility?: Visibility;
  /** Departments that can view this resource (null = all departments) */
  department_restrictions?: Department[] | null;
}

/**
 * Request for replacing a resource's file with a new version.
 */
export interface ResourceReplaceRequest {
  /** Upload session ID from upload-url */
  upload_id: string;
  /**
   * New filename
   * @maxLength 255
   */
  filename: string;
  /** MIME type of the new file */
  mime_type: string;
  /**
   * File size in bytes
   * @exclusiveMinimum 0
   */
  file_size: number;
  /** Optional notes about changes */
  change_notes?: string | null;
}

/**
 * Request for updating resource metadata.
 */
export interface ResourceUpdate {
  title?: string | null;
  description?: string | null;
  category?: string | null;
  tags?: string[] | null;
  /** Departments that can view this resource (null = all departments) */
  department_restrictions?: Department[] | null;
}

/**
 * Represents a version in the resource history.
 */
export interface ResourceVersion {
  version: number;
  filename?: string | null;
  file_size?: number | null;
  uploaded_by: string;
  uploaded_by_name?: string | null;
  uploaded_at: string;
  change_notes?: string | null;
  is_current: boolean;
  download_url?: string | null;
}

/**
 * Request for restoring a previous version.
 */
export interface RestoreVersionRequest {
  /**
   * Version number to restore
   * @exclusiveMinimum 0
   */
  version: number;
}

/**
 * Coaching role assignment for a user role.
 */
export interface RoleAssignment {
  user_role: string;
  coaching_role_ids: string[];
  default_role_id?: string | null;
}

/**
 * List of all role assignments.
 */
export interface RoleAssignmentListResponse {
  assignments: RoleAssignment[];
}

export type SearchResponseCategories = { [key: string]: number };

/**
 * Types of search results.
 */
export type SearchResultType = (typeof SearchResultType)[keyof typeof SearchResultType];

export const SearchResultType = {
  navigation: "navigation",
  goal: "goal",
  milestone: "milestone",
  task: "task",
  conversation: "conversation",
  transcript: "transcript",
  resource: "resource",
  enrollment: "enrollment",
  course: "course",
} as const;

export type SearchResultMetadata = { [key: string]: unknown };

/**
 * A single search result.
 */
export interface SearchResult {
  type: SearchResultType;
  id: string;
  title: string;
  snippet?: string | null;
  url: string;
  metadata?: SearchResultMetadata;
  score?: number | null;
}

/**
 * Response from search API.
 */
export interface SearchResponse {
  results: SearchResult[];
  total: number;
  query: string;
  query_time_ms: number;
  categories: SearchResponseCategories;
}

/**
 * Scope for transcript search.
 */
export type SearchScope = (typeof SearchScope)[keyof typeof SearchScope];

export const SearchScope = {
  TITLE: "TITLE",
  SENTENCES: "SENTENCES",
  ALL: "ALL",
} as const;

/**
 * Request to select preferred avatar style.
 */
export interface SelectStyleRequest {
  /** Selected style */
  style: AvatarStyle;
}

/**
 * Response from selecting avatar style.
 */
export interface SelectStyleResponse {
  success: boolean;
}

export interface SessionRefreshRequest {
  /** @minLength 1 */
  firebase_token: string;
}

export interface SessionRefreshResponse {
  session_expires_at: string;
  message?: string;
}

/**
 * Response schema for session settings.
 */
export interface SessionSettingsResponse {
  inactivity_enabled?: boolean;
  /**
   * @minimum 5
   * @maximum 480
   */
  inactivity_timeout_minutes?: number;
  /**
   * @minimum 1
   * @maximum 10
   */
  warning_period_minutes?: number;
  updated_at?: string | null;
  updated_by?: string | null;
}

/**
 * Request schema for updating session settings.
 */
export interface SessionSettingsUpdate {
  inactivity_enabled?: boolean | null;
  inactivity_timeout_minutes?: number | null;
  warning_period_minutes?: number | null;
}

export interface SessionStatusResponse {
  authenticated: boolean;
  expires_at?: string | null;
  user?: UserProfileResponse | null;
}

/**
 * Request to set user's default coaching role.
 */
export interface SetDefaultRoleRequest {
  role_id: string;
}

/**
 * Request to set the organization calendar.
 */
export interface SetOrgCalendarRequest {
  /** Calendar ID to use for company events */
  calendar_id: string;
}

/**
 * Generic success response for operations without specific return data.
 */
export interface SuccessResponse {
  success?: boolean;
  message?: string | null;
}

/**
 * Aggregate metrics for a team.
 */
export interface TeamAggregateMetrics {
  avg_progress: number;
  on_track_count: number;
  behind_count: number;
  at_risk_count: number;
  total_goals: number;
  completed_goals: number;
  active_users: number;
  total_ai_queries?: number;
  avg_training_completion?: number | null;
  pending_creation_count?: number;
  pending_completion_count?: number;
  department_breakdown?: DepartmentBreakdown | null;
}

/**
 * Call analytics for a single team member (manager view).
 */
export interface TeamMemberAnalytics {
  user_id: string;
  display_name?: string | null;
  call_count?: number;
  average_score?: number | null;
  latest_score?: number | null;
  latest_tier?: string | null;
  trend?: string | null;
}

/**
 * Aggregated team call analytics for managers.
 */
export interface TeamCallAnalytics {
  total_team_calls?: number;
  team_average_score?: number | null;
  members?: TeamMemberAnalytics[];
  from_date?: string | null;
  to_date?: string | null;
}

/**
 * Summary of a team member's goals.
 */
export interface TeamMemberGoalSummary {
  user_id: string;
  display_name: string;
  email: string;
  total_goals: number;
  active_goals: number;
  completed_goals: number;
  average_progress: number;
  overdue_count: number;
}

/**
 * Response for team goals view.
 */
export interface TeamGoalsResponse {
  team_members: TeamMemberGoalSummary[];
  total_members: number;
  team_average_progress: number;
}

/**
 * Status of team member's progress.
 */
export type TeamMemberStatus = (typeof TeamMemberStatus)[keyof typeof TeamMemberStatus];

export const TeamMemberStatus = {
  on_track: "on_track",
  behind: "behind",
  at_risk: "at_risk",
} as const;

/**
 * Progress information for a single team member.
 */
export interface TeamMemberProgress {
  user_id: string;
  display_name: string;
  role: string;
  job_title?: string | null;
  photo_url?: string | null;
  department?: string | null;
  progress_percent: number;
  last_active_at?: string | null;
  status: TeamMemberStatus;
  is_manager?: boolean;
  direct_report_count?: number;
  ai_query_count?: number;
  last_login_at?: string | null;
  training_completion_rate?: number | null;
  training_completed?: number;
  training_total?: number;
  pending_creation_count?: number;
  pending_completion_count?: number;
}

/**
 * Training summary for a team member.
 */
export interface TeamMemberTraining {
  user_id: string;
  display_name: string;
  email: string;
  total_enrollments: number;
  completed_count: number;
  in_progress_count: number;
  completion_rate: number;
}

/**
 * Training progress for a single team member in reports.
 */
export interface TeamMemberTrainingProgress {
  user_id: string;
  display_name: string;
  completion_rate: number;
  completed_count: number;
  total_enrollments: number;
  thinkific_connected: boolean;
}

/**
 * Response for team members list with progress.
 */
export interface TeamMembersResponse {
  members: TeamMemberProgress[];
  total: number;
  aggregate?: TeamAggregateMetrics | null;
}

/**
 * Response for the Team Overview tab.
 */
export interface TeamOverviewResponse {
  completion_rate: number;
  completion_rate_trend: MetricTrend;
  avg_learning_days: number;
  avg_learning_days_trend: MetricTrend;
  ai_queries_per_hour: number;
  ai_queries_trend: MetricTrend;
  engagement_score: number;
  engagement_trend: MetricTrend;
  completion_trend: TimeSeriesPoint[];
  learning_time_trend: TimeSeriesPoint[];
}

/**
 * Team training overview for managers.
 */
export interface TeamTrainingResponse {
  team_members: TeamMemberTraining[];
  total_members: number;
  average_completion_rate: number;
}

/**
 * Response schema for a document template.
 */
export interface TemplateResponse {
  id: string;
  name: string;
  description: string;
  format: string;
  data_sources: string[];
  prompt: string;
  is_system: boolean;
  enabled: boolean;
  template_type?: string;
  template_file_path?: string | null;
}

/**
 * Response schema for listing templates.
 */
export interface TemplateListResponse {
  templates: TemplateResponse[];
}

/**
 * Request to connect Thinkific via API key.
 */
export interface ThinkificApiKeyConnectRequest {
  /**
   * @minLength 1
   * @maxLength 100
   * @pattern ^[a-z0-9-]+$
   */
  subdomain: string;
  /** @minLength 1 */
  api_key: string;
}

/**
 * Bundle from Thinkific (a grouping of courses).
 */
export interface ThinkificBundle {
  id: number;
  name: string;
  course_count: number;
  course_ids?: number[];
}

/**
 * List of available bundles.
 */
export interface ThinkificBundleListResponse {
  bundles: ThinkificBundle[];
  total: number;
}

/**
 * Request to initiate Thinkific OAuth connection.
 */
export interface ThinkificConnectRequest {
  /**
   * @minLength 1
   * @maxLength 100
   * @pattern ^[a-z0-9-]+$
   */
  subdomain: string;
}

/**
 * Course from Thinkific.
 */
export interface ThinkificCourse {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  course_card_image_url?: string | null;
  chapter_count?: number;
  is_free?: boolean;
  published?: boolean;
}

/**
 * List of courses.
 */
export interface ThinkificCourseListResponse {
  courses: ThinkificCourse[];
  total: number;
}

/**
 * User enrollment in a course.
 */
export interface ThinkificEnrollment {
  id: number;
  course_id: number;
  course_name: string;
  course_slug: string;
  course_image_url?: string | null;
  percentage_completed?: number;
  completed?: boolean;
  activated_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  expired?: boolean;
}

/**
 * User's enrollments.
 */
export interface ThinkificEnrollmentListResponse {
  enrollments: ThinkificEnrollment[];
  total: number;
  completed_count: number;
  in_progress_count: number;
  syncing?: boolean;
  last_synced_at?: string | null;
}

/**
 * Request from OAuth callback with authorization code.
 */
export interface ThinkificOAuthCallbackRequest {
  code: string;
  state: string;
}

/**
 * Response with OAuth authorization URL.
 */
export interface ThinkificOAuthStartResponse {
  authorization_url: string;
  state: string;
}

/**
 * Request to update Thinkific settings.
 */
export interface ThinkificSettingsUpdateRequest {
  bundle_id?: number | null;
  additional_course_ids?: number[];
}

/**
 * Sync status for Thinkific integration.
 */
export type ThinkificSyncStatus = (typeof ThinkificSyncStatus)[keyof typeof ThinkificSyncStatus];

export const ThinkificSyncStatus = {
  idle: "idle",
  syncing: "syncing",
  error: "error",
} as const;

/**
 * Thinkific connection status.
 */
export interface ThinkificStatusResponse {
  connected: boolean;
  subdomain?: string | null;
  connected_at?: string | null;
  last_sync_at?: string | null;
  sync_status?: ThinkificSyncStatus;
  sync_error?: string | null;
  course_count?: number;
  bundle_id?: number | null;
  bundle_name?: string | null;
  bundle_course_count?: number;
  additional_course_ids?: number[];
}

/**
 * Response from sync operation.
 */
export interface ThinkificSyncResponse {
  success: boolean;
  message: string;
  courses_synced: number;
  enrollments_synced: number;
  users_matched: number;
  verified_task_count?: number;
}

/**
 * Details of a task verified via Thinkific.
 */
export interface ThinkificVerifyTaskDetail {
  task_id: string;
  task_title?: string | null;
  course_id: number;
  course_name?: string | null;
  completed_at?: string | null;
}

/**
 * Response from manual task verification.
 */
export interface ThinkificVerifyTasksResponse {
  success: boolean;
  verified_count: number;
  already_completed: number;
  pending_courses: number;
  no_enrollment: number;
  details?: ThinkificVerifyTaskDetail[];
}

/**
 * Response for the Training tab in Reports.
 */
export interface TrainingOverviewResponse {
  avg_completion_rate: number;
  avg_completion_rate_trend: MetricTrend;
  total_completed: number;
  total_enrolled: number;
  connected_users: number;
  total_users: number;
  completion_trend: TimeSeriesPoint[];
  members: TeamMemberTrainingProgress[];
}

/**
 * Admin-configured training requirement.
 */
export interface TrainingRequirement {
  id: string;
  course_id: number;
  course_name: string;
  roles: string[];
  departments?: string[];
  required: boolean;
  due_days_from_start?: number | null;
  created_at: string;
  created_by: string;
}

/**
 * List of training requirements.
 */
export interface TrainingRequirementListResponse {
  requirements: TrainingRequirement[];
  total: number;
}

/**
 * Response with list of imported transcripts.
 */
export interface TranscriptListResponse {
  transcripts: ImportedTranscriptSummary[];
  total: number;
  cursor?: string | null;
  has_more: boolean;
}

/**
 * Summary info for an unassigned user.
 */
export interface UnassignedUserResponse {
  uid: string;
  email: string;
  display_name?: string | null;
  role: UserRole;
  created_at: string;
  last_login_at: string;
}

/**
 * Response containing list of users without managers.
 */
export interface UnassignedUsersResponse {
  users: UnassignedUserResponse[];
  total: number;
}

/**
 * Request to update a coaching role.
 */
export interface UpdateCoachingRoleRequest {
  name?: string | null;
  description?: string | null;
  system_prompt?: string | null;
  intro_message?: string | null;
  resource_ids?: string[] | null;
  conversation_starters?: string[] | null;
  icon_type?: IconType | null;
  icon_value?: string | null;
  ownership_type?: OwnershipType | null;
  partner_name?: string | null;
  enabled?: boolean | null;
  display_order?: number | null;
  is_default?: boolean | null;
  department_access?: Department[] | null;
  rag_enabled?: boolean | null;
  rag_include_conversations?: boolean | null;
  rag_min_relevance_score?: number | null;
  rag_source_display_threshold?: number | null;
  include_identity_context?: boolean | null;
  include_goals_detail?: boolean | null;
  include_training_summary?: boolean | null;
  include_resources_summary?: boolean | null;
  include_team_context?: boolean | null;
}

/**
 * Request to update a conversation.
 */
export interface UpdateConversationRequest {
  /** @maxLength 100 */
  title: string;
}

/**
 * Request to update department assignment.
 */
export interface UpdateDepartmentAssignmentRequest {
  coaching_role_ids: string[];
  default_role_id?: string | null;
}

/**
 * Request to update an existing goal.
 */
export interface UpdateGoalRequest {
  title?: string | null;
  description?: string | null;
  category?: GoalCategory | null;
  target_date?: string | null;
  status?: GoalStatus | null;
}

/**
 * Request to update meeting category.
 */
export interface UpdateMeetingCategoryRequest {
  category: MeetingCategory;
}

/**
 * Request to update a milestone.
 */
export interface UpdateMilestoneRequest {
  title?: string | null;
  description?: string | null;
  target_date?: string | null;
  weight?: number | null;
  status?: MilestoneStatus | null;
}

/**
 * Request to update an existing resource category.
 */
export interface UpdateResourceCategoryRequest {
  /** Display name */
  name?: string | null;
  /** Help text for the category */
  description?: string | null;
  /** Sort order (0-based) */
  display_order?: number | null;
}

/**
 * Request to update coaching roles for a resource.
 */
export interface UpdateResourceCoachingRolesRequest {
  coaching_role_ids: string[];
}

/**
 * Request to update role assignment.
 */
export interface UpdateRoleAssignmentRequest {
  coaching_role_ids: string[];
  default_role_id?: string | null;
}

export interface UpdateRoleRequest {
  /** @minLength 1 */
  uid: string;
  role: UserRole;
}

export interface UpdateRoleResponse {
  user: UserProfileResponse;
  message?: string;
}

/**
 * Request to update a subtask.
 */
export interface UpdateSubtaskRequest {
  title?: string | null;
}

/**
 * Request to update a task.
 */
export interface UpdateTaskRequest {
  title?: string | null;
  description?: string | null;
  priority?: TaskPriority | null;
  due_date?: string | null;
  status?: TaskStatus | null;
  linked_course_id?: number | null;
  linked_course_name?: string | null;
  subtasks?: SubtaskInput[] | null;
}

/**
 * Request to update a template.
 */
export interface UpdateTemplateRequest {
  name?: string | null;
  description?: string | null;
  format?: string | null;
  data_sources?: string[] | null;
  prompt?: string | null;
  enabled?: boolean | null;
  template_type?: string | null;
}

/**
 * Request to update individual user assignment.
 */
export interface UpdateUserAssignmentRequest {
  coaching_role_ids: string[];
}

/**
 * Request to update a user's department.
 */
export interface UpdateUserDepartmentRequest {
  /** Department to assign. Set to null to unassign. */
  department?: Department | null;
}

/**
 * Request for updating resource visibility and department restrictions.
 */
export interface UpdateVisibilityRequest {
  /** New visibility setting (optional) */
  visibility?: Visibility | null;
  /** Departments that can access this resource. Null means no department restrictions. */
  department_restrictions?: Department[] | null;
}

/**
 * Request for generating a signed upload URL.
 */
export interface UploadUrlRequest {
  /**
   * Original filename
   * @maxLength 255
   */
  filename: string;
  /** MIME type of the file */
  content_type: string;
  /**
   * File size in bytes (max 500MB)
   * @maximum 524288000
   * @exclusiveMinimum 0
   */
  file_size: number;
}

/**
 * Response containing signed upload URL.
 */
export interface UploadUrlResponse {
  /** Signed URL for direct upload to GCS */
  upload_url: string;
  /** Upload session ID */
  upload_id: string;
  /** GCS path where file will be stored */
  storage_path: string;
  /** When the signed URL expires */
  expires_at: string;
}

/**
 * Information about a user's Outlook calendar.
 */
export interface UserCalendarInfo {
  /** Calendar ID */
  id: string;
  /** Calendar name */
  name: string;
  /** Is the default calendar */
  is_default?: boolean;
  /** User can edit this calendar */
  can_edit?: boolean;
}

/**
 * Response listing user's available Outlook calendars.
 */
export interface UserCalendarsResponse {
  calendars: UserCalendarInfo[];
}

/**
 * Individual user coaching role assignment.
 */
export interface UserCoachingAssignment {
  user_id: string;
  coaching_role_ids: string[];
}

/**
 * Response containing version history for a resource.
 */
export interface VersionHistoryResponse {
  current_version: number;
  versions: ResourceVersion[];
}

/**
 * Webhook configuration status.
 */
export interface WebhookConfigResponse {
  has_secret: boolean;
  secret_preview?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  webhook_url: string;
}

/**
 * A single webhook event record.
 */
export interface WebhookEventResponse {
  id: string;
  event_type: string;
  resource: string;
  action: string;
  user_email?: string | null;
  user_uid?: string | null;
  thinkific_user_id?: number | null;
  course_id?: number | null;
  course_name?: string | null;
  percentage_completed?: number | null;
  status: string;
  error_message?: string | null;
  source_ip?: string | null;
  received_at: string;
  processed_at?: string | null;
}

export type WebhookEventSummaryResponseCounts = { [key: string]: number };

/**
 * Summary of webhook event statistics.
 */
export interface WebhookEventSummaryResponse {
  hours: number;
  counts: WebhookEventSummaryResponseCounts;
  success_rate: number;
}

/**
 * List of webhook events.
 */
export interface WebhookEventsListResponse {
  events: WebhookEventResponse[];
  total: number;
}

/**
 * Response containing the newly generated webhook secret.
 */
export interface WebhookSecretResponse {
  secret: string;
  webhook_url: string;
  message: string;
}

export type GetAgentToolAnalyticsParams = {
  /**
   * Inclusive start date (YYYY-MM-DD)
   */
  from_date: string;
  /**
   * Inclusive end date (YYYY-MM-DD)
   */
  to_date: string;
  /**
   * Filter by tool name
   */
  tool?: string | null;
  /**
   * Filter by outcome (success | empty | error)
   */
  outcome?: string | null;
};

export type PreviewOrgEventsParams = {
  /**
   * Start of date range (defaults to today)
   */
  start_date?: string | null;
  /**
   * End of date range (defaults to 30 days from now)
   */
  end_date?: string | null;
};

export type ListCallAnalysesParams = {
  /**
   * Start date filter
   */
  from_date?: string | null;
  /**
   * End date filter
   */
  to_date?: string | null;
  /**
   * Filter by coaching role
   */
  coaching_role_id?: string | null;
  /**
   * Max results to return
   * @minimum 1
   * @maximum 100
   */
  limit?: number;
};

export type GetCallAnalysisSummaryParams = {
  /**
   * Start date filter
   */
  from_date?: string | null;
  /**
   * End date filter
   */
  to_date?: string | null;
};

export type GetTeamCallAnalyticsParams = {
  /**
   * Start date filter
   */
  from_date?: string | null;
  /**
   * End date filter
   */
  to_date?: string | null;
};

export type GetCompanyEventsParams = {
  /**
   * Start of date range (defaults to today)
   */
  start_date?: string | null;
  /**
   * End of date range (defaults to 30 days from now)
   */
  end_date?: string | null;
};

export type ListConversationsParams = {
  /**
   * @minimum 1
   * @maximum 100
   */
  limit?: number;
  cursor?: string | null;
};

export type AddInterruptedMessage201 = { [key: string]: string };

export type ApproveTaskCreationParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type RejectTaskCreationParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type SubmitTaskForCompletionParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type ApproveTaskCompletionParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type RejectTaskCompletionParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type ManagerMarkTaskCompleteParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type ListGoalsParams = {
  status?: GoalStatus | null;
  category?: GoalCategory | null;
  /**
   * @minimum 1
   * @maximum 100
   */
  limit?: number;
  include_milestones?: boolean;
};

export type GetTeamMemberGoalsParams = {
  status?: GoalStatus | null;
  category?: GoalCategory | null;
};

export type GetAssignableTeamMembers200Item = { [key: string]: unknown };

export type GetGoalParams = {
  include_milestones?: boolean;
};

export type UpdateMilestoneParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
};

export type DeleteMilestoneParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
};

export type CreateTaskParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
};

export type UpdateTaskParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type DeleteTaskParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type ToggleTaskParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type ReorderTasksParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
};

export type AddSubtaskParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type UpdateSubtaskParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type DeleteSubtaskParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type ToggleSubtaskParams = {
  /**
   * Parent goal ID
   */
  goal_id: string;
  /**
   * Parent milestone ID
   */
  milestone_id: string;
};

export type ListFirefliesTranscriptsParams = {
  /**
   * @minimum 1
   * @maximum 50
   */
  limit?: number;
  /**
   * @minimum 0
   */
  skip?: number;
};

export type SearchFirefliesTranscriptsParams = {
  /**
   * @minLength 1
   * @maxLength 255
   */
  keyword: string;
  scope?: SearchScope;
  /**
   * @minimum 1
   * @maximum 50
   */
  limit?: number;
};

export type ListSyncedEventsParams = {
  /**
   * Filter by source type
   */
  source_type?: string | null;
};

export type ImportMeetingsParams = {
  /**
   * Start of date range
   */
  start_date: string;
  /**
   * End of date range
   */
  end_date: string;
  /**
   * Filter by category
   */
  category?: MeetingCategory | null;
};

export type GetTeamOverviewParams = {
  range?: DateRange;
};

export type GetTeamMembersParams = {
  range?: DateRange;
  /**
   * Optional manager ID to drill down into. If not provided, shows current user's team.
   */
  manager_id?: string | null;
};

export type GetLearningProgressParams = {
  range?: DateRange;
};

export type GetEngagementParams = {
  range?: DateRange;
};

export type GetAiUsageParams = {
  range?: DateRange;
};

export type ExportTeamReportParams = {
  range?: DateRange;
  format?: ExportFormat;
};

export type GetTrainingOverviewParams = {
  range?: DateRange;
};

export type GetCoachingUsageParams = {
  range?: DateRange;
};

export type GetApprovalWorkflowMetricsParams = {
  range?: DateRange;
};

export type ListResourcesParams = {
  /**
   * @minimum 1
   */
  page?: number;
  /**
   * @minimum 1
   * @maximum 100
   */
  page_size?: number;
  category?: string | null;
  file_type?: ResourceFileType | null;
  /**
   * Comma-separated tags
   */
  tags?: string | null;
  search?: string | null;
  include_deleted?: boolean;
};

export type SearchParams = {
  /**
   * Search query (min 2 characters)
   * @minLength 2
   */
  q: string;
  /**
   * Max results per category
   * @minimum 1
   * @maximum 10
   */
  limit?: number;
  /**
   * Comma-separated result types to filter
   */
  types?: string | null;
};

export type GetWebhookEventsParams = {
  limit?: number;
  status_filter?: string | null;
};

export type GetWebhookEventSummaryParams = {
  hours?: number;
};

export type GetTrainingRequirementsParams = {
  /**
   * Filter by role
   */
  role?: string | null;
  /**
   * Filter by department
   */
  department?: string | null;
};

export type GetRequirementPreviewParams = {
  /**
   * Comma-separated roles
   */
  roles?: string | null;
  /**
   * Comma-separated departments
   */
  departments?: string | null;
};

export type ListTranscriptsParams = {
  /**
   * @minimum 1
   * @maximum 100
   */
  limit?: number;
  cursor?: string | null;
};

export type ListKbItemsParams = {
  kind?: KbItemKind | null;
  enrichment_status?: EnrichmentStatus | null;
  archived?: boolean | null;
  promoted?: boolean | null;
  q?: string | null;
  from?: string | null;
  to?: string | null;
  cursor?: string | null;
  /**
   * @minimum 1
   * @maximum 200
   */
  limit?: number;
};

export type ItemsExistParams = {
  /**
   * @minItems 1
   * @maxItems 50
   */
  item_ids: string[];
};

export type HubspotCallbackParams = {
  code: string;
  state: string;
};

export type HubspotDisconnect200 = { [key: string]: string };

export type HubspotAdminConfig200 = { [key: string]: string };

export type HubspotAdminRevokeAll200 = { [key: string]: unknown };

export type AutotaskAdminConfig200 = { [key: string]: string };

export type EnrichYoutubeVideo200 = { [key: string]: unknown };

export type EnrichMsLearnDoc200 = { [key: string]: unknown };

export type EnrichVulnerability200 = { [key: string]: unknown };

export type EnrichRoadmapItem200 = { [key: string]: unknown };

export type EnrichPricingSnapshot200 = { [key: string]: unknown };

export type EnrichUrl200 = { [key: string]: unknown };
